
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ValidationObserver } from 'vee-validate';
import { DateTime } from 'luxon';

import { sharedState } from '../../../../../framework/state';
import { getResource, postResource, putResource } from '../../../../../framework/client/resource';
import { getEnumerationOptions, OptionValue } from '../../../../../framework/service/options';

import DateField from '../../../../../framework/fields/DateField.vue';
import NumberField from '../../../../../framework/fields/NumberField.vue';
import CurrencyAmountField from '../../../../../framework/fields/CurrencyAmountField.vue';
import PercentageField from '../../../../../framework/fields/PercentageField.vue';
import TextField from '../../../../../framework/fields/TextField.vue';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import SwitchField from '../../../../../framework/fields/SwitchField.vue';
import EmailField from '../../../../../framework/fields/EmailField.vue';

import { VehicleState } from '../../../../../../common/application/model/enums/VehicleState';
import { VehicleContract } from '../../../../../../common/application/model/VehicleContract';
import { VehicleType } from '../../../../../../common/application/model/enums/VehicleType';
import { VehicleIntendedUse } from '../../../../../../common/application/model/enums/VehicleIntendedUse';
import { VehicleUsageType } from '../../../../../../common/application/model/enums/VehicleUsageType';
import { VehicleEnginePowerType } from '../../../../../../common/application/model/enums/VehicleEnginePowerType';
import { VehicleElectricalClass } from '../../../../../../common/application/model/enums/VehicleElectricalClass';
import { FinancingContractType } from '../../../../../../common/application/model/enums/FinancingContractType';
import { SmartFleetContractType } from '../../../../../../common/application/model/enums/SmartFleetContractType';
import { AccountType } from '../../../../../../common/application/model/enums/AccountType';

import VendorContracts from '../vendor/VendorContracts.vue';
import AutoCompleteMultipleField from '../../../../fields/AutoCompleteMultipleField.vue';
import VehicleContractPurchaseInvoices from '../../invoice/purchase/VehicleContractPurchaseInvoices.vue';
import VehicleContractComments from './comment/VehicleContractComments.vue';
import VehicleUsageReports from '../../../manager/report/VehicleUsageReports.vue';
import AddDocument from '../../document/AddDocument.vue';
import UsageField from '../../../../fields/UsageField.vue';
import PartPaymentPanel from './PartPaymentPanel.vue';

import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { VehicleMake } from '../../../../../../common/application/model/VehicleMake';
import { VehicleMakeType } from '../../../../../../common/application/model/enums/VehicleMakeType';
import { SpecialUseVehicleType } from '../../../../../../common/application/model/enums/SpecialUseVehicleType';

import { successToast, errorToast } from '../../../../service/toast_service';
import { updateCachedResourceRow } from '../../../../service/query_cache_service';
import {
    getAccountOptions,
    getCustomerOptions,
    getVendorOptions,
} from '../../../../service/application_options_service';
import { dateToDbDateString } from '../../../../../../common/framework/util/convert';
import VehicleContractFillRequests from './VehicleContractFillRequests.vue';

@Component({
    components: {
        VendorContracts,
        EmailField,
        UsageField,
        PercentageField,
        DateField,
        NumberField,
        SwitchField,
        SelectField,
        TextField,
        CurrencyAmountField,
        ValidationObserver,
        VehicleContractComments,
        VehicleContractPurchaseInvoices,
        AddDocument,
        AutoCompleteMultipleField,
        VehicleUsageReports,
        VehicleContractFillRequests,
        PartPaymentPanel,
    },
    beforeRouteEnter(to: any, from: any, next: any) {
        next((vm: EditVehicleContract) => {
            vm.getPrevRouter(from); //get the previous URL
        });
    },
})
export default class EditVehicleContract extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.VEHICLE_CONTRACT;

    // Properties
    @Prop(String) readonly id!: string;

    // State
    shared = sharedState;
    local = {
        row: {} as VehicleContract,
        customerOptions: [] as OptionValue[],
        vehicleTypeOptions: [] as OptionValue[],
        vehicleStateOptions: [] as OptionValue[],
        vehicleIntendedUseOptions: [] as OptionValue[],
        vehicleUsageTypeOptions: [] as OptionValue[],
        vehicleEnginePowerTypeOptions: [] as OptionValue[],
        vehicleElectricalClassOptions: [] as OptionValue[],
        financingContractTypeOptions: [] as OptionValue[],
        smartFleetContractTypeOptions: [] as OptionValue[],
        customerExpenseAccountOptions: [] as OptionValue[],
        vendorOptions: [] as OptionValue[],
        specialUseVehicleTypeOptions: [] as OptionValue[],
        showAllVehicleFields: false,
        showAllSmartFleetFields: false,
        showAllFinancingFields: false,
        loading: true,
        hasVehicleTracking: false,
        totalComments: 0,
        activeTab: 0,
        prevRoute: '',
        vehicleMakeId: [] as string[],
        showPaymentRows: false,
    };

    // Functions
    async mounted() {
        this.local.loading = true;

        this.local.activeTab = this.$route.params.activeTab
            ? Number(this.$route.params.activeTab)
            : Number(localStorage.getItem(`${this.resourceType}_ActiveTab`));
        this.local.row = (await getResource<VehicleContract>(this.resourceType, this.id))!!;

        this.local.customerOptions = await getCustomerOptions();
        this.local.vehicleTypeOptions = getEnumerationOptions('VehicleType', Object.keys(VehicleType), this);
        this.local.vehicleStateOptions = getEnumerationOptions('VehicleState', Object.keys(VehicleState), this);
        this.local.vehicleIntendedUseOptions = getEnumerationOptions(
            'VehicleIntendedUse',
            Object.keys(VehicleIntendedUse),
            this,
        );
        this.local.vehicleUsageTypeOptions = getEnumerationOptions(
            'VehicleUsageType',
            Object.keys(VehicleUsageType),
            this,
        );
        this.local.vehicleEnginePowerTypeOptions = getEnumerationOptions(
            'VehicleEnginePowerType',
            Object.keys(VehicleEnginePowerType),
            this,
        );
        this.local.vehicleElectricalClassOptions = getEnumerationOptions(
            'VehicleElectricalClass',
            Object.keys(VehicleElectricalClass),
            this,
        );

        this.local.financingContractTypeOptions = getEnumerationOptions(
            'FinancingContractType',
            Object.keys(FinancingContractType),
            this,
        );
        this.local.smartFleetContractTypeOptions = getEnumerationOptions(
            'SmartFleetContractType',
            Object.keys(SmartFleetContractType),
            this,
        );
        this.local.specialUseVehicleTypeOptions = getEnumerationOptions(
            'SpecialUseVehicleType',
            Object.keys(SpecialUseVehicleType),
            this,
        );

        this.local.customerExpenseAccountOptions = await getAccountOptions(
            this.local.row.customerId,
            AccountType.EXPENSE,
        );
        this.local.vendorOptions = await getVendorOptions();
        localStorage.removeItem(`${this.resourceType}_ActiveTab`);

        this.local.loading = false;
    }

    @Watch('local.row.customerId')
    async customerIdChange() {
        this.local.customerExpenseAccountOptions = await getAccountOptions(
            this.local.row.customerId,
            AccountType.EXPENSE,
        );
    }

    @Watch('local.row.smartFleetContractStart')
    smartFleetContractStartChange(smartFleetContractStart: any | undefined) {
        const focusedElement = document.activeElement as Element & { name: string };

        if (this.local.loading || !focusedElement || !this.isActiveDatePicker(focusedElement.classList)) {
            return;
        }

        if (!smartFleetContractStart || !this.local.row.smartFleetContractEnd) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            smartFleetContractPeriodMonths: this.calculateContractPeriod(
                smartFleetContractStart,
                this.local.row.smartFleetContractEnd as any,
            ),
        };
    }

    @Watch('local.row.smartFleetContractEnd')
    smartFleetContractEndChange(smartFleetContractEnd: any | undefined) {
        const focusedElement = document.activeElement as Element & { name: string };

        if (this.local.loading || !focusedElement || !this.isActiveDatePicker(focusedElement.classList)) {
            return;
        }

        if (!smartFleetContractEnd || !this.local.row.smartFleetContractStart) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            smartFleetContractPeriodMonths: this.calculateContractPeriod(
                this.local.row.smartFleetContractStart as any,
                smartFleetContractEnd,
            ),
        };
    }

    @Watch('local.row.smartFleetContractPeriodMonths')
    smartFleetContractPeriodMonthsChange(smartFleetContractPeriodMonths: number | undefined) {
        const focusedElement = document.activeElement as Element & { name: string };

        if (this.local.loading || !focusedElement || focusedElement.name !== 'smartFleetContractPeriodMonths') {
            return;
        }

        if (!smartFleetContractPeriodMonths || !this.local.row.smartFleetContractStart) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            smartFleetContractEnd: this.calculateEndDate(
                this.local.row.smartFleetContractStart as any,
                smartFleetContractPeriodMonths as number,
            ),
        };
    }

    @Watch('local.row.financingContractStart')
    financingContractStartChange(financingContractStart: any | undefined) {
        const focusedElement = document.activeElement as Element & { name: string };

        if (this.local.loading || !focusedElement || !this.isActiveDatePicker(focusedElement.classList)) {
            return;
        }

        if (!financingContractStart || !this.local.row.financingContractEnd) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            financingContractPeriodMonths: this.calculateContractPeriod(
                financingContractStart,
                this.local.row.financingContractEnd as any,
            ),
        };
    }

    @Watch('local.row.financingContractEnd')
    financingContractEndChange(financingContractEnd: any | undefined) {
        const focusedElement = document.activeElement as Element & { name: string };

        if (this.local.loading || !focusedElement || !this.isActiveDatePicker(focusedElement.classList)) {
            return;
        }

        if (!financingContractEnd || !this.local.row.financingContractStart) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            financingContractPeriodMonths: this.calculateContractPeriod(
                this.local.row.financingContractStart as any,
                financingContractEnd,
            ),
        };
    }

    @Watch('local.row.financingContractPeriodMonths')
    financingContractPeriodMonthsChange(financingContractPeriodMonths: number | undefined) {
        const focusedElement = document.activeElement as Element & { name: string };

        if (this.local.loading || !focusedElement || focusedElement.name !== 'financingContractPeriodMonths') {
            return;
        }

        if (!financingContractPeriodMonths || !this.local.row.financingContractStart) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            financingContractEnd: this.calculateEndDate(
                this.local.row.financingContractStart as any,
                financingContractPeriodMonths as number,
            ),
        };
    }

    @Watch('local.row.financingContractType')
    showPaymentRows() {
        this.local.showPaymentRows = this.local.row.financingContractType === FinancingContractType.PART_PAYMENT;
    }

    dropdownClosed(vehicleMakeIds: string[], idField: string): void {
        if (!vehicleMakeIds[0]) {
            return;
        }

        this.local.row = {
            ...this.local.row,
            vehicleMakeId: vehicleMakeIds[0],
        };
    }

    copyDateValues(source: string) {
        if (source === 'smartfleet') {
            this.local.row = {
                ...this.local.row,
                financingContractStart: this.local.row.smartFleetContractStart,
                financingContractEnd: this.local.row.smartFleetContractEnd,
                financingContractPeriodMonths: this.local.row.smartFleetContractPeriodMonths,
            };
        } else {
            this.local.row = {
                ...this.local.row,
                smartFleetContractStart: this.local.row.financingContractStart,
                smartFleetContractEnd: this.local.row.financingContractEnd,
                smartFleetContractPeriodMonths: this.local.row.financingContractPeriodMonths,
            };
        }
    }

    async addVehicleMake(name: string) {
        this.local.loading = true;
        try {
            const newVehicleMake: VehicleMake = {
                id: '',
                created: new Date(),
                modified: new Date(),
                name,
                description: '',
                type: VehicleMakeType.CAR,
            };

            const vehicleMake = await postResource<VehicleMake>(ApplicationResource.VEHICLE_MAKE, newVehicleMake);

            setTimeout(() => (this.local.vehicleMakeId = [vehicleMake.id]), 1000);
        } catch (e) {
            errorToast(this, 'error.couldNotInsertVehicleMake');
        }
        this.local.loading = true;
    }

    get vehicleMakeId(): string[] {
        return this.local.row.vehicleMakeId ? [this.local.row.vehicleMakeId] : [];
    }

    back() {
        this.$router.go(-1);
    }

    getPrevRouter(from: any) {
        this.local.prevRoute = from.path;
        if (this.local.prevRoute.includes('/file-meta/')) {
            this.local.activeTab = 3;
        }
    }

    save() {
        this.saveResource(false);
    }

    saveAndClose() {
        this.saveResource(true);
    }

    private calculateContractPeriod(start: string, end: string): number {
        return Math.round(
            DateTime.fromFormat(end, 'yyyy-MM-dd').diff(DateTime.fromFormat(start, 'yyyy-MM-dd'), 'months').months,
        );
    }

    private calculateEndDate(start: string, duration: number): Date {
        return dateToDbDateString(
            DateTime.fromFormat(start, 'yyyy-MM-dd').plus({ months: duration }).toJSDate(),
        ) as any;
    }

    private isActiveDatePicker(classList: DOMTokenList): boolean {
        return classList.value === 'datepicker-cell is-selectable';
    }

    private async saveResource(goBack: boolean) {
        try {
            if (!this.local.row.smartFleetContractHandoverDate) {
                this.local.row = {
                    ...this.local.row,
                    smartFleetContractHandoverDate: this.local.row.smartFleetContractStart,
                };
            }

            const vehicleMake = (await getResource<VehicleMake>(
                ApplicationResource.VEHICLE_MAKE,
                this.local.row.vehicleMakeId,
            ))!!;
            const updatedRow = await putResource<VehicleContract>(this.resourceType, this.id, {
                ...this.local.row,
                vehicleMake: vehicleMake.name,
            });

            successToast(this, 'message.vehicleContractSaved');
            updateCachedResourceRow<VehicleContract>(updatedRow, this.id, this.resourceType);

            if (goBack) {
                this.back();
            }
        } catch (error) {
            errorToast(this);
        }
    }
}
